import { Box, Typography } from '@material-ui/core'
import AvailabilityCheckInput from 'components/AvailabilityCheckInput'
import VZFInput from 'components/VZFInput'
import ViewWrapper from 'components/ViewWrapper'
import React, { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AvailabilityCheckField } from 'store/AvailabilityCheck/AvailabilityCheck.actions'
import { ViewType } from 'store/GeneralState/GeneralState.reducer'
import { getTestIDOnViewType } from 'utils/TestIDs'
import useURLParams from 'utils/URLParamsContex'
import { getRoleAwareTranslationKey, useUserInfo } from 'utils/UserInfoContext'
import { useAvailabilityCheckReducer } from './useAvailabilityCheckReducer'

const AvailabilityCheck = (): JSX.Element => {
    const availabilitySubmitRef = useRef<HTMLButtonElement>(null)
    const {
        availableCities,
        availableHouseNumbers,
        availableStreets,
        availableAddition,
        cities,
        currentView,
        customizeJsData,
        focusedFieldId,
        houseNumbers,
        streets,
        additions,
        zip,
        vzf,
        vzfNoResult,
        vzfLoading,
        selectedCity,
        setLoadState,
        selectedHouseNumber,
        selectedStreet,
        selectedDistrict,
        selectedAddition,
        setSelectedCityAndDistrict,
        setSelectedHouseNumber,
        setSelectedStreet,
        setSelectedAddition,
        setCities,
        setHouseNumbers,
        setStreets,
        setFocusedField,
        setZip,
        setAdditions,
        onChangeVZF,
        onClickCheckVZF,
        getAvailableCities,
        getAvailableHouseNumbers,
        getAvailableStreets,
        getAvailableAdditions,
    } = useAvailabilityCheckReducer()

    const { t } = useTranslation()

    const { vzf: paramsVZF, B2B } = useURLParams()

    const enableAddressAddition = customizeJsData?.globalConfiguration.enableAddressAddition ?? false

    useEffect(() => {
        if (paramsVZF && !B2B) onChangeVZF(paramsVZF)
    }, [paramsVZF])

    useEffect(() => {
        if (availableCities.data) {
            setCities(availableCities.data.availableCities)
        }
    }, [availableCities, setCities])

    useEffect(() => {
        if (cities.length === 1 && selectedCity.length === 0) {
            setSelectedCityAndDistrict({ city: cities[0].city, district: cities[0].district })
        } else if (cities.length > 1) {
            setFocusedField(AvailabilityCheckField.CITY)
        }
    }, [cities, setFocusedField, setSelectedCityAndDistrict, selectedCity])

    useEffect(() => {
        if (zip !== '' && selectedCity !== '') {
            getAvailableStreets({
                variables: {
                    zipcode: zip,
                    city: selectedCity,
                    district: selectedDistrict || undefined,
                },
            })
        }
    }, [selectedCity, getAvailableStreets, zip, selectedDistrict])

    useEffect(() => {
        if (availableStreets.data) {
            setStreets(availableStreets.data.availableStreets)
        }
    }, [availableStreets, setStreets])

    useEffect(() => {
        if (streets.length === 1) {
            setSelectedStreet(streets[0])
        } else if (streets.length > 1) {
            setFocusedField(AvailabilityCheckField.STREET)
        }
    }, [streets, setFocusedField, setSelectedStreet])

    useEffect(() => {
        if (selectedStreet !== '' && zip !== '' && selectedCity !== '') {
            getAvailableHouseNumbers({
                variables: {
                    zipcode: zip,
                    city: selectedCity,
                    street: selectedStreet,
                    district: selectedDistrict,
                },
            })
        }
    }, [selectedStreet, getAvailableHouseNumbers, zip, selectedCity, selectedDistrict])

    useEffect(() => {
        if (availableHouseNumbers.data) {
            setHouseNumbers(availableHouseNumbers.data.availableHouseNumbers)
        }
    }, [availableHouseNumbers, setHouseNumbers, setSelectedHouseNumber])

    useEffect(() => {
        if (houseNumbers.length === 1) {
            setSelectedHouseNumber(houseNumbers[0])
            setFocusedField(AvailabilityCheckField.SUBMIT)
        } else if (houseNumbers.length > 1) {
            setFocusedField(AvailabilityCheckField.HOUSE_NUMBER)
        }
    }, [houseNumbers, setFocusedField, setSelectedHouseNumber])

    useEffect(() => {
        if (selectedHouseNumber !== '') {
            setFocusedField(AvailabilityCheckField.SUBMIT)
        }
    }, [selectedHouseNumber, setFocusedField])

    useEffect(() => {
        if (selectedHouseNumber !== '' && enableAddressAddition && zip !== '' && selectedCity !== '') {
            getAvailableAdditions({
                variables: {
                    zipcode: zip,
                    city: selectedCity,
                    street: selectedStreet,
                    district: selectedDistrict,
                    houseNumber: selectedHouseNumber,
                },
            })
        }
    }, [selectedStreet, getAvailableAdditions, zip, selectedCity, selectedDistrict, selectedHouseNumber])

    useEffect(() => {
        if (availableAddition.data) {
            setAdditions(availableAddition.data.availableAdditions)
        }
    }, [availableAddition, setStreets])

    useEffect(() => {
        if (additions.length === 1) {
            setSelectedAddition(additions[0])
        } else if (additions.length > 1) {
            setFocusedField(AvailabilityCheckField.ADDITION)
        }
    }, [additions, setFocusedField, setSelectedAddition])

    useEffect(() => {
        if (focusedFieldId === AvailabilityCheckField.SUBMIT && availabilitySubmitRef.current !== null) {
            availabilitySubmitRef.current.focus()
        }
    }, [focusedFieldId])

    const noProductLink = useMemo(() => {
        if (customizeJsData) {
            return customizeJsData.noProductAvailable.link
        } else {
            return ''
        }
    }, [customizeJsData])

    const [userInfo] = useUserInfo()
    let disabledSubmit = selectedHouseNumber === '' || zip.length === 0 || selectedCity === ''
    let submitButtonText = undefined
    if (!customizeJsData?.globalConfiguration.vzfCheckOwnButton) {
        disabledSubmit = vzf.length === 0 && (selectedHouseNumber === '' || zip.length === 0 || selectedCity === '')
        submitButtonText = vzf.length > 0 ? t('vzf.vzfCheck') : undefined
    }

    const enableProductWithoutAddress =
        customizeJsData?.globalConfiguration.enableProductWithoutAddress && zip.length === 5 && cities.length === 0

    return (
        <ViewWrapper
            disableBasket={true}
            submitButtonText={submitButtonText}
            disabledSubmit={disabledSubmit && !enableProductWithoutAddress}
            submitRef={availabilitySubmitRef}
            header={getRoleAwareTranslationKey(userInfo, customizeJsData, 'availabilityCheckHeader')}
            subHeader={''}
            viewType={currentView}
            disableBackButton={!enableProductWithoutAddress}
            onSubmitAlt={vzf.length > 0 ? onClickCheckVZF : undefined}
            enableVZFInput={
                (!enableProductWithoutAddress && !B2B && customizeJsData?.globalConfiguration.vzfCheckOwnButton) ??
                false
            }
            vzf={vzf}
            vzfNoResult={vzfNoResult}
            vzfLoading={vzfLoading}
            onClickCheckVZF={onClickCheckVZF}
            onChangeVZF={onChangeVZF}
        >
            <>
                {enableProductWithoutAddress ? (
                    <Box pb={2}>
                        <Typography
                            {...getTestIDOnViewType(ViewType.AVAILABILITY_CHECK, 'SUBHEADER_TYPOGRAPHY')}
                            variant={'h4'}
                            dangerouslySetInnerHTML={{
                                __html: t(
                                    getRoleAwareTranslationKey(
                                        userInfo,
                                        customizeJsData,
                                        'availabilityCheckSubheaderNoProduct',
                                    ),
                                ),
                            }}
                        />
                    </Box>
                ) : (
                    <>
                        <Box pb={2}>
                            <Typography
                                {...getTestIDOnViewType(ViewType.AVAILABILITY_CHECK, 'SUBHEADER_TYPOGRAPHY')}
                                variant={'h4'}
                            >
                                {t(getRoleAwareTranslationKey(userInfo, customizeJsData, 'availabilityCheckSubheader'))}{' '}
                            </Typography>
                        </Box>
                        <AvailabilityCheckInput
                            enableAddressAddition={enableAddressAddition}
                            haveLink={customizeJsData?.noProductAvailable.haveLink}
                            noProductLink={noProductLink}
                            disabledCity={cities.length < 2}
                            disabledHouseNumber={houseNumbers.length < 2}
                            disabledStreet={streets.length < 2}
                            disabledAddition={additions.length < 2}
                            disabledZip={false}
                            cities={cities}
                            focusedFieldId={focusedFieldId}
                            houseNumbers={houseNumbers}
                            additions={additions}
                            queriesAreFinished={
                                availableCities.loading === false &&
                                availableCities.called === true &&
                                (availableCities.data !== undefined || availableCities.error !== undefined) &&
                                availableStreets.loading === false &&
                                availableStreets.called === true &&
                                (availableStreets.data !== undefined || availableStreets.error !== undefined) &&
                                availableHouseNumbers.loading === false &&
                                availableHouseNumbers.called === true &&
                                (availableHouseNumbers.data !== undefined ||
                                    availableHouseNumbers.error !== undefined) &&
                                availableAddition.loading === false &&
                                availableAddition.called === true &&
                                (availableAddition.data !== undefined || availableAddition.error !== undefined)
                            }
                            selectedCity={selectedCity}
                            selectedDistrict={selectedDistrict}
                            selectedHouseNumber={selectedHouseNumber}
                            selectedStreet={selectedStreet}
                            selectedAddition={selectedAddition}
                            setFocusedField={setFocusedField}
                            setSelectedCityAndDistrict={(
                                payload: React.ChangeEvent<{
                                    name?: string | undefined
                                    value: unknown
                                }>,
                            ): void => {
                                const displayValue = payload.target.value as string
                                const cityItem = cities.find(
                                    (c) => (c.district ? `${c.city} - ${c.district}` : c.city) === displayValue,
                                )
                                if (cityItem) {
                                    setSelectedCityAndDistrict({ city: cityItem.city, district: cityItem.district })
                                }
                                setStreets([])
                                setHouseNumbers([])
                                setAdditions([])
                                setSelectedAddition('')
                            }}
                            setSelectedHouseNumber={(
                                payload: React.ChangeEvent<{
                                    name?: string | undefined
                                    value: unknown
                                }>,
                            ): void => {
                                setAdditions([])
                                setSelectedAddition('')
                                setSelectedHouseNumber(payload.target.value as string)
                            }}
                            setSelectedStreet={(
                                payload: React.ChangeEvent<{
                                    name?: string | undefined
                                    value: unknown
                                }>,
                            ): void => {
                                setSelectedStreet(payload.target.value as string)
                                setSelectedHouseNumber('')
                                setHouseNumbers([])
                                setAdditions([])
                                setSelectedAddition('')
                            }}
                            setZip={(
                                payload: React.ChangeEvent<{
                                    name?: string | undefined
                                    value: unknown
                                }>,
                            ): void => {
                                setSelectedCityAndDistrict({ city: '', district: '' })
                                setCities([])
                                setStreets([])
                                setSelectedHouseNumber('')
                                setHouseNumbers([])
                                setAdditions([])
                                setSelectedAddition('')
                                setZip(payload.target.value as string)
                                if ((payload.target.value as string).length === 5) {
                                    setLoadState({
                                        loading: true,
                                        errors: undefined,
                                    })
                                    getAvailableCities({ variables: { zipcode: payload.target.value as string } })
                                }
                            }}
                            setSelectedAddition={(
                                payload: React.ChangeEvent<{
                                    name?: string | undefined
                                    value: unknown
                                }>,
                            ): void => {
                                setSelectedAddition(payload.target.value as string)
                            }}
                            streets={streets}
                            zip={zip}
                            autoFocusOnMount={true}
                        />
                    </>
                )}
                {!B2B && !customizeJsData?.globalConfiguration.vzfCheckOwnButton && !enableProductWithoutAddress && (
                    <VZFInput
                        vzf={vzf}
                        loading={vzfLoading}
                        onEnter={onClickCheckVZF}
                        noResult={vzfNoResult}
                        onChangeVZF={onChangeVZF}
                    />
                )}
            </>
        </ViewWrapper>
    )
}

export default AvailabilityCheck
